import { useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ManualAnswerModal } from './components/manual-answer-modal';
import { KimOnboarding } from './components/onboarding/kim-onboarding';
import { ModeSelector } from './components/question/mode-selector';
import { QuestionInput } from './components/question/question-input';
import { QuestionSidebar } from './components/question/question-sidebar';
import { ChecklistStep, DraftType } from '../../../../../../../../../backend/src/types/types';
import { Icon } from '../../../../../../components/icon';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../../../../../../components/shadcn/empty';
import { useMatterProcessingStore } from '../../../../../../stores/matter-processing-store';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useCreateMessage } from '@/api/mutations/useCreateMessage';
import { useCreateThread } from '@/api/mutations/useCreateThread';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import useGetUser from '@/api/queries/useGetUser';
import { QueryEntity } from '@/api/types';
import { sanitiseKimQuestion } from '@/helpers/sanitiseKimQuestion';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Button } from '@/new/components/shadcn/button';
import { SubsetChip } from '@/new/components/subset-chip';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { ExecuteKimSearch, KimMode } from '@/types';

const getTitleFromMode = (mode: KimMode) => {
  switch (mode) {
    case KimMode.QuestionFlow:
      return 'Ask a question...';
    case KimMode.ExtractionFlow:
      return 'Extract information...';
    case KimMode.DraftFlow:
      return 'Draft me a...';
  }
};

export function Kim() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const [questionValue, setQuestionValue] = useState('');
  const [draftType, setDraftType] = useState<DraftType | null>(null);
  const [mode, setMode] = useState<KimMode>(KimMode.QuestionFlow);
  const [taggedDocuments, setTaggedDocuments] = useState<{ doc_id: string; file_name: string }[]>([]);
  const [placeholder, setPlaceholder] = useState('Tell me about X...');
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [isManualAnswerModalOpen, setIsManualAnswerModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: responseDocIds } = useGetDocIds(subsetId);
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { data: user } = useGetUser();

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Kim', path: 'kim' },
  });
  const { mutate: createThread, isLoading: isCreatingThread } = useCreateThread(subsetId);
  const { mutate: createMessage, isLoading: isCreatingMessage } = useCreateMessage();

  const hasCompletedKimOnboarding = user && !user.onboardingChecklistItems?.includes(ChecklistStep.UseKim);

  const resetState = () => {
    setQuestionValue('');
    setTaggedDocuments([]);
    setMode(KimMode.QuestionFlow);
    setDraftType(null);
  };

  const executeSearch: ExecuteKimSearch = async ({ question: value, draftType, questionMode }) => {
    if (!value || value.trim() === '') {
      return;
    }
    const question = sanitiseKimQuestion(value);
    createThread(question, {
      onSuccess: (data) => {
        createMessage({
          caseId: subsetId,
          question,
          docIds: taggedDocuments.map((doc) => doc.doc_id),
          draftType,
          mode: questionMode,
          threadId: data.id,
        });

        navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${data.id}`);
        trackCustomEvent(TrackingEventsV2.AskedKimQuestion, {
          matterId,
          mode,
          isAllDocuments: subset?.isAllDocuments,
        });
      },
      onError: (error) => {
        console.error('Error creating thread and message');
        toast.error('Error processing your question. Please try again.');
      },
      onSettled: () => {
        resetState();
        queryClient.invalidateQueries({ queryKey: [QueryEntity.User] });
      },
    });
  };

  // Ask a question from the onboarding
  useEffect(() => {
    const { question, mode } = location.state || {};
    if (question && mode) {
      setQuestionValue(question);
      setMode(mode);
      executeSearch({ question: question, draftType: null, questionMode: mode });
    }
  }, [location.state]);

  useEffect(() => {
    trackPageView(PageName.Kim);
  }, []);

  useEffect(() => {
    setDraftType(null);
  }, [mode]);

  useEffect(() => {
    if (hasCompletedKimOnboarding) {
      setShowOnboarding(false);
    }
  }, [hasCompletedKimOnboarding]);

  const { getProcessingState } = useMatterProcessingStore();

  if (getProcessingState(matterId)?.hasCompletedRuns === false) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--bot" />
          </EmptyMedia>
          <EmptyTitle>Kim Unavailable</EmptyTitle>
          <EmptyDescription>Kim is unavailable while documents are still processing.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />

      <div className="relative flex h-full w-full flex-1 items-center overflow-hidden">
        {hasCompletedKimOnboarding && (
          <>
            <QuestionSidebar taggedDocuments={taggedDocuments} />
            <div className="absolute top-4 right-4 flex gap-2">
              {user?.organisation?.isLabellingOrg && (
                <Button
                  onClick={() => setIsManualAnswerModalOpen(true)}
                  className="flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100"
                >
                  <span>+ Create Manual Answer</span>
                </Button>
              )}
              <Button
                onClick={() => {
                  if (!showOnboarding) {
                    trackCustomEvent(TrackingEventsV2.KimClickHelp);
                  }
                  setShowOnboarding(!showOnboarding);
                }}
                className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm"
              >
                <span
                  className={`${showOnboarding ? 'iconify lucide--arrow-left text-gray-500' : 'iconify lucide--circle-help text-teal-500'}`}
                ></span>

                <span>{showOnboarding ? 'Back' : 'Need some help?'}</span>
              </Button>
            </div>
          </>
        )}

        <div className="flex w-full flex-col items-center justify-center">
          {/* Show onboarding in center if user hasn't completed any Kim items yet */}
          {user && showOnboarding ? (
            <KimOnboarding
              executeSearch={executeSearch}
              setMode={setMode}
              setDraftType={setDraftType}
              setQuestionValue={setQuestionValue}
              subset={subset}
            />
          ) : (
            <div className="flex w-full max-w-4xl flex-col items-center px-4">
              <h2 className="mb-6 flex flex-col items-center gap-2.5 text-2xl font-semibold text-gray-800">
                <SubsetChip subsetId={subsetId} />
                <span className="pl-2">{getTitleFromMode(mode)}</span>
              </h2>

              <QuestionInput
                executeSearch={executeSearch}
                onChange={(v: string) => setQuestionValue(v)}
                value={questionValue}
                docs={responseDocIds?.documents ?? []}
                placeholder={placeholder}
                taggedDocuments={taggedDocuments}
                setTaggedDocuments={setTaggedDocuments}
                draftType={draftType}
                setDraftType={setDraftType}
                mode={mode}
                jurisdiction={subset?.jurisdiction ?? []}
                isCreatingThread={isCreatingThread || isCreatingMessage}
              />
              <ModeSelector mode={mode} setMode={setMode} setPlaceholder={setPlaceholder} />
            </div>
          )}
        </div>
      </div>

      {/* Manual Answer Modal */}
      <ManualAnswerModal
        isOpen={isManualAnswerModalOpen}
        onClose={() => setIsManualAnswerModalOpen(false)}
        onComplete={(threadId) => {
          setIsManualAnswerModalOpen(false);
          navigate(`/matters/${matterId}/subsets/${subsetId}/kim/${threadId}`);
        }}
      />
    </div>
  );
}
